export interface PrivateChatStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type PrivateChatSurface = 'bos' | 'hos';

const USER_KEY = 'boss_user';
const PRIVATE_BASE_KEYS = [
  'boss_chat_orb_log',
  'boss_office_threads_v1',
  'boss_office_active_thread_v1',
  'boss_office_avatar_v1',
] as const;
const LEGACY_GLOBAL_KEYS = [
  'boss_chat_orb_log',
  'boss_hos_chat_orb_log',
  'boss_office_threads_v1',
  'boss_office_active_thread_v1',
  'boss_hos_assistant_threads_v1',
  'boss_hos_assistant_active_thread_v1',
  'boss_office_avatar_v1',
] as const;

function browserStorage(): PrivateChatStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function principalScope(storage: PrivateChatStorage | null): string | null {
  if (!storage) return null;
  try {
    const parsed = JSON.parse(storage.getItem(USER_KEY) ?? 'null') as {
      id?: unknown;
      tenantId?: unknown;
    } | null;
    const userId = typeof parsed?.id === 'string' ? parsed.id : '';
    const tenantId = typeof parsed?.tenantId === 'string' ? parsed.tenantId : '';
    if (!userId || userId.trim() !== userId || !tenantId || tenantId.trim() !== tenantId) return null;
    return `${encodeURIComponent(tenantId)}:${encodeURIComponent(userId)}`;
  } catch {
    return null;
  }
}

export function privateChatStorageKey(
  base: typeof PRIVATE_BASE_KEYS[number],
  surface: PrivateChatSurface,
  storage: PrivateChatStorage | null = browserStorage(),
): string | null {
  const scope = principalScope(storage);
  return scope ? `${base}:${surface}:${scope}` : null;
}

export function purgePrivateChatStorage(
  storage: PrivateChatStorage | null = browserStorage(),
): void {
  if (!storage) return;
  const scopedKeys = PRIVATE_BASE_KEYS.flatMap((base) => (
    (['bos', 'hos'] as const)
      .map((surface) => privateChatStorageKey(base, surface, storage))
      .filter((key): key is string => Boolean(key))
  ));
  for (const key of [...LEGACY_GLOBAL_KEYS, ...scopedKeys]) storage.removeItem(key);
}
